import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useSyncedReload } from "@/hooks/useSyncedReload";
import { useLang } from "@/context/LanguageContext";
import { Field, inputCls } from "@/components/shared";

const WEEKDAYS = { en: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"], fr: ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"] };
const localDate = (d = new Date()) => d.toLocaleDateString("sv-SE");

const TYPE_STYLES = {
  viewing: "bg-[#2F4F4F] text-white",
  task: "bg-slate-100 text-slate-800 border border-slate-200",
  follow_up: "bg-orange-50 text-[#9A3412] border border-orange-100",
  decision: "bg-stone-100 text-stone-600 border border-stone-200",
};
const TYPE_LINKS = { viewing: "/viewings", task: "/tasks", follow_up: "/buyers", decision: "/viewings" };

const QuickAddDialog = ({ date, onClose, onSaved }) => {
  const { t } = useLang();
  const [tab, setTab] = useState("viewing");
  const [task, setTask] = useState({ title: "", priority: "medium" });
  const [viewing, setViewing] = useState({ buyer_id: "", buyer_name: "", property_id: "", property_name: "", time: "10:00" });
  const [buyers, setBuyers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/buyers").then((r) => setBuyers(r.data)).catch(() => {});
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (tab === "task") {
        if (!task.title.trim()) return toast.error(t("required"));
        await api.post("/tasks", { ...task, due_date: date, status: "pending" });
      } else {
        if (!viewing.buyer_name.trim() || !viewing.property_name.trim()) return toast.error(t("required"));
        const { time, ...rest } = viewing;
        await api.post("/viewings", { ...rest, date_time: `${date}T${time || "10:00"}` });
      }
      toast.success(t("saved"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-medium text-stone-900">{date}</DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-stone-100 h-auto p-1">
            <TabsTrigger value="viewing" data-testid="quickadd-tab-viewing">{t("viewing")}</TabsTrigger>
            <TabsTrigger value="task" data-testid="quickadd-tab-task">{t("tasks")}</TabsTrigger>
          </TabsList>
          <TabsContent value="viewing">
            <div className="space-y-4 mt-3">
              <Field label={t("buyer")}>
                <Select value={viewing.buyer_id || "custom"}
                  onValueChange={(v) => {
                    const b = buyers.find((x) => x.id === v);
                    setViewing({ ...viewing, buyer_id: v === "custom" ? "" : v, buyer_name: b ? b.name : viewing.buyer_name });
                  }}>
                  <SelectTrigger data-testid="quickadd-buyer-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">—</SelectItem>
                    {buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <input data-testid="quickadd-buyer-name" className={`${inputCls} mt-2`} placeholder={t("name")}
                  value={viewing.buyer_name} onChange={(e) => setViewing({ ...viewing, buyer_name: e.target.value })} />
              </Field>
              <Field label={t("property")}>
                <Select value={viewing.property_id || "custom"}
                  onValueChange={(v) => {
                    const p = properties.find((x) => x.id === v);
                    setViewing({ ...viewing, property_id: v === "custom" ? "" : v, property_name: p ? p.name : viewing.property_name });
                  }}>
                  <SelectTrigger data-testid="quickadd-property-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">—</SelectItem>
                    {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <input data-testid="quickadd-property-name" className={`${inputCls} mt-2`} placeholder={t("name")}
                  value={viewing.property_name} onChange={(e) => setViewing({ ...viewing, property_name: e.target.value })} />
              </Field>
              <Field label={t("time")}>
                <input data-testid="quickadd-time-input" type="time" className={inputCls}
                  value={viewing.time} onChange={(e) => setViewing({ ...viewing, time: e.target.value })} />
              </Field>
            </div>
          </TabsContent>
          <TabsContent value="task">
            <div className="space-y-4 mt-3">
              <Field label={t("taskTitle")}>
                <input data-testid="quickadd-task-title" className={inputCls}
                  value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} />
              </Field>
              <Field label={t("priority")}>
                <Select value={task.priority} onValueChange={(v) => setTask({ ...task, priority: v })}>
                  <SelectTrigger data-testid="quickadd-priority-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">{t("high")}</SelectItem>
                    <SelectItem value="medium">{t("medium")}</SelectItem>
                    <SelectItem value="low">{t("low")}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </TabsContent>
        </Tabs>
        <div className="flex justify-end gap-3 mt-4">
          <button data-testid="quickadd-cancel" onClick={onClose}
            className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-5 py-2 text-sm font-medium transition-colors">{t("cancel")}</button>
          <button data-testid="quickadd-save" onClick={save} disabled={saving}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2 text-sm font-medium transition-colors disabled:opacity-60">{t("save")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Calendar() {
  const now = new Date();
  const [cursor, setCursor] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [events, setEvents] = useState([]);
  const [selectedDay, setSelectedDay] = useState(localDate());
  const [quickAdd, setQuickAdd] = useState(null);
  const { t, lang } = useLang();

  const monthStr = `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}`;

  const load = useCallback(() => {
    api.get("/calendar", { params: { month: monthStr } }).then((r) => setEvents(r.data.events)).catch(() => {});
  }, [monthStr]);
  useEffect(() => { load(); }, [load]);
  useSyncedReload(load);

  const byDate = events.reduce((acc, e) => {
    (acc[e.date] = acc[e.date] || []).push(e);
    return acc;
  }, {});

  const firstDow = (new Date(cursor.y, cursor.m, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const todayStr = localDate();
  const monthTitle = new Date(cursor.y, cursor.m, 1).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { month: "long", year: "numeric" });

  const navMonth = (dir) => {
    const d = new Date(cursor.y, cursor.m + dir, 1);
    setCursor({ y: d.getFullYear(), m: d.getMonth() });
  };

  const typeLabel = { viewing: t("viewing"), task: t("tasks"), follow_up: t("followUpEvent"), decision: t("decisionEvent") };
  const dayEvents = byDate[selectedDay] || [];

  return (
    <div data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-stone-900 capitalize">{monthTitle}</h1>
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev-month" onClick={() => navMonth(-1)}
            className="p-2 bg-white border border-stone-200 rounded-lg text-stone-500 hover:text-stone-900 hover:border-stone-300 transition-colors"><ChevronLeft size={16} /></button>
          <button data-testid="calendar-today-button"
            onClick={() => { setCursor({ y: now.getFullYear(), m: now.getMonth() }); setSelectedDay(todayStr); }}
            className="px-4 py-2 bg-white border border-stone-200 rounded-lg text-xs font-medium text-stone-600 hover:text-stone-900 hover:border-stone-300 transition-colors">{t("todayBtn")}</button>
          <button data-testid="calendar-next-month" onClick={() => navMonth(1)}
            className="p-2 bg-white border border-stone-200 rounded-lg text-stone-500 hover:text-stone-900 hover:border-stone-300 transition-colors"><ChevronRight size={16} /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-6">
        {/* Month grid */}
        <div className="bg-white border border-stone-200 rounded-xl p-3 sm:p-5">
          <div className="grid grid-cols-7 mb-2">
            {WEEKDAYS[lang].map((d) => (
              <div key={d} className="text-center text-[10px] uppercase tracking-[0.12em] font-semibold text-stone-400 py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDow }).map((_, i) => <div key={`b${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${monthStr}-${String(day).padStart(2, "0")}`;
              const evts = byDate[dateStr] || [];
              const isToday = dateStr === todayStr;
              const isSelected = dateStr === selectedDay;
              let cellBorder = "border-stone-100 bg-white hover:border-stone-300";
              if (isSelected) cellBorder = "border-slate-900 bg-stone-50";
              else if (isToday) cellBorder = "border-[#9F8E7E] bg-white";
              return (
                <button
                  key={dateStr}
                  data-testid={`calendar-day-${dateStr}`}
                  onClick={() => setSelectedDay(dateStr)}
                  className={`min-h-[64px] sm:min-h-[84px] rounded-lg border p-1.5 text-left transition-colors flex flex-col gap-1 ${cellBorder}`}
                >
                  <span className={`text-xs font-semibold ${isToday ? "text-[#9F8E7E]" : "text-stone-600"}`}>{day}</span>
                  <div className="space-y-0.5 overflow-hidden">
                    {evts.slice(0, 2).map((e, idx) => (
                      <span key={`${e.type}-${e.id}-${idx}`}
                        className={`block truncate rounded px-1 py-px text-[9px] sm:text-[10px] font-medium leading-tight ${TYPE_STYLES[e.type]} ${e.status === "done" ? "opacity-40 line-through" : ""}`}>
                        {e.time ? `${e.time} ` : ""}{e.title}
                      </span>
                    ))}
                    {evts.length > 2 && <span className="block text-[9px] text-stone-400">+{evts.length - 2} {t("moreEvents")}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected day panel */}
        <div className="bg-white border border-stone-200 rounded-xl p-5 h-fit">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h2 className="font-heading text-lg font-medium text-stone-900">
              {new Date(`${selectedDay}T12:00`).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { weekday: "long", day: "numeric", month: "long" })}
            </h2>
            <button data-testid="calendar-quick-add-button" onClick={() => setQuickAdd(selectedDay)}
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-3 py-2 text-xs font-medium transition-colors">
              <Plus size={13} strokeWidth={1.5} /> {t("add")}
            </button>
          </div>
          {dayEvents.length === 0 ? (
            <p className="text-sm text-stone-400 py-6 text-center">{t("nothingToday")}</p>
          ) : (
            <div className="space-y-2.5">
              {dayEvents.map((e, idx) => (
                <div key={`${e.type}-${e.id}-${idx}`} data-testid={`day-event-${e.type}-${e.id}`}
                  className="border border-stone-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${TYPE_STYLES[e.type]}`}>{typeLabel[e.type]}</span>
                    {e.time && <span className="text-xs font-semibold text-stone-700">{e.time}</span>}
                    {e.priority === "high" && <span className="text-[10px] text-[#9A3412] font-medium">{t("high")}</span>}
                  </div>
                  <Link to={TYPE_LINKS[e.type]} className={`block text-sm font-medium mt-1.5 text-stone-800 hover:text-slate-900 ${e.status === "done" ? "line-through text-stone-400" : ""}`}>
                    {e.title}
                  </Link>
                  {e.type === "viewing" && (
                    <a data-testid={`ics-day-${e.id}`} href={`${API}/viewings/${e.id}/ics`}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-stone-500 hover:text-stone-900 border border-stone-200 hover:border-stone-300 rounded-lg px-2.5 py-1.5 transition-colors">
                      <CalendarPlus size={12} strokeWidth={1.5} /> {t("addToCalendar")}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {quickAdd && <QuickAddDialog date={quickAdd} onClose={() => setQuickAdd(null)} onSaved={load} />}
    </div>
  );
}
